import type { Mission } from "./types";

export type BoardDropMove = { missionId: string; aircraftId: string };

export type BoardDropTail = { id: string; icaoType: string; registration: string };

export type BoardDropBar = {
  missionId: string;
  aircraftId: string;
  startMs: number;
  endMs: number;
};

export type TailLock = {
  aircraftId: string;
  holder: string;
  registration?: string;
  /** Company that owns the lock. A different company does not see it. */
  vaId?: string;
};

function lockVisible(row: TailLock, vaId?: string): boolean {
  if (!row.aircraftId || !row.holder.trim()) return false;
  if (vaId && row.vaId && row.vaId !== vaId) return false;
  return true;
}

/** Holder of a tail in this company, or empty when the tail is free here. */
export function lockHolder(locks: readonly TailLock[] | undefined, aircraftId: string, vaId?: string): string {
  const row = (locks ?? []).find((lock) => lock.aircraftId === aircraftId && lockVisible(lock, vaId));
  return row?.holder.trim() ?? "";
}

/** Refusal copy when a move would change a hard-locked tail. Names the holder. */
export function tailLockBlock(input: {
  fromAircraftId?: string;
  toAircraftId: string;
  fleet: readonly { id: string; registration: string }[];
  locks?: readonly TailLock[];
  vaId?: string;
}): string | null {
  const name = (id: string) => input.fleet.find((row) => row.id === id)?.registration || id;
  if (input.fromAircraftId && input.fromAircraftId !== input.toAircraftId) {
    const holder = lockHolder(input.locks, input.fromAircraftId, input.vaId);
    if (holder) return `${name(input.fromAircraftId)} is held by ${holder}.`;
  }
  const holder = lockHolder(input.locks, input.toAircraftId, input.vaId);
  if (holder) return `${name(input.toAircraftId)} is held by ${holder}.`;
  return null;
}

/** Where a dragged bar lands. One move, or a same-type swap when the target overlaps. */
export function resolveBarDrop(input: {
  missionId: string;
  fromAircraftId: string;
  toAircraftId: string;
  dragged: { startMs: number; endMs: number };
  fleet: readonly BoardDropTail[];
  bars: readonly BoardDropBar[];
  locks?: readonly TailLock[];
  vaId?: string;
}): { ok: true; moves: BoardDropMove[]; swap: boolean } | { ok: false; error: string } {
  if (!input.toAircraftId || input.toAircraftId === input.fromAircraftId) {
    return { ok: true, moves: [], swap: false };
  }
  const target = input.fleet.find((tail) => tail.id === input.toAircraftId);
  if (!target) return { ok: false, error: "That tail is not on the fleet book." };
  const block = tailLockBlock({
    fromAircraftId: input.fromAircraftId,
    toAircraftId: input.toAircraftId,
    fleet: input.fleet,
    locks: input.locks,
    vaId: input.vaId,
  });
  if (block) return { ok: false, error: block };
  const source = input.fleet.find((tail) => tail.id === input.fromAircraftId);
  const overlap = input.bars.find(
    (bar) =>
      bar.aircraftId === input.toAircraftId &&
      bar.missionId !== input.missionId &&
      bar.startMs < input.dragged.endMs &&
      bar.endMs > input.dragged.startMs,
  );
  const sameType = Boolean(source && source.icaoType && source.icaoType === target.icaoType);
  if (overlap && sameType) {
    return {
      ok: true,
      swap: true,
      moves: [
        { missionId: input.missionId, aircraftId: input.toAircraftId },
        { missionId: overlap.missionId, aircraftId: input.fromAircraftId },
      ],
    };
  }
  return { ok: true, swap: false, moves: [{ missionId: input.missionId, aircraftId: input.toAircraftId }] };
}

export function laneMismatches(
  bars: readonly { missionId: string; originIcao: string; destIcao: string; startMs: number }[],
): { fromId: string; toId: string }[] {
  const ordered = [...bars].sort((a, b) => a.startMs - b.startMs);
  const out: { fromId: string; toId: string }[] = [];
  for (let i = 1; i < ordered.length; i += 1) {
    const prev = ordered[i - 1];
    const next = ordered[i];
    if (!prev || !next) continue;
    if (prev.destIcao.trim().toUpperCase() !== next.originIcao.trim().toUpperCase()) {
      out.push({ fromId: prev.missionId, toId: next.missionId });
    }
  }
  return out;
}

export function laneConnectors(
  bars: readonly { missionId: string; tripKey: string; startMs: number }[],
): { fromId: string; toId: string }[] {
  const ordered = [...bars].sort((a, b) => a.startMs - b.startMs);
  const out: { fromId: string; toId: string }[] = [];
  for (let i = 1; i < ordered.length; i += 1) {
    const prev = ordered[i - 1];
    const next = ordered[i];
    if (!prev || !next || !prev.tripKey || prev.tripKey !== next.tripKey) continue;
    out.push({ fromId: prev.missionId, toId: next.missionId });
  }
  return out;
}

export function rotationPush<T extends { id: string; aircraftId: string; departureIso: string; rotationHold?: boolean }>(
  missions: readonly T[],
  lateId: string,
  nextDepartureIso: string,
): { updates: { id: string; departureIso: string }[]; heldIds: string[] } {
  const late = missions.find((row) => row.id === lateId);
  const nextMs = new Date(nextDepartureIso).getTime();
  const prevMs = late ? new Date(late.departureIso).getTime() : Number.NaN;
  if (!late || !Number.isFinite(nextMs) || !Number.isFinite(prevMs) || nextMs === prevMs) {
    return { updates: [], heldIds: [] };
  }
  const delta = nextMs - prevMs;
  const updates = [{ id: late.id, departureIso: new Date(nextMs).toISOString() }];
  const heldIds: string[] = [];
  for (const row of missions) {
    if (row.id === late.id || row.aircraftId !== late.aircraftId) continue;
    const rowMs = new Date(row.departureIso).getTime();
    if (!Number.isFinite(rowMs) || rowMs < prevMs) continue;
    if (row.rotationHold) {
      heldIds.push(row.id);
      continue;
    }
    updates.push({ id: row.id, departureIso: new Date(rowMs + delta).toISOString() });
  }
  return { updates, heldIds };
}

export function copiedMission(source: Mission, now = new Date()): Mission {
  const dep = new Date(source.departureIso);
  const departureIso = Number.isFinite(dep.getTime())
    ? new Date(dep.getTime() + 24 * 60 * 60 * 1000).toISOString()
    : now.toISOString();
  const stamp = now.getTime().toString(36);
  const flight = source.flightNumber || source.callsign;
  return {
    ...source,
    id: `ns-copy-${source.id}-${stamp}`,
    createdAt: now.toISOString(),
    departureIso,
    callsign: flight ? `${flight}C` : source.callsign,
    flightNumber: flight ? `${flight}C` : source.flightNumber,
    status: "pending",
    lifecycle: "scheduled",
    boardChange: "new",
    rotationHold: undefined,
    hoursApplied: undefined,
    ledgerPosted: undefined,
    completedAt: undefined,
    cancelledAt: undefined,
    cancelReason: undefined,
    cancelReasonNote: undefined,
    parentMissionId: undefined,
    inboxRequestId: undefined,
    actualDepartureUtc: undefined,
    arrivedAtUtc: undefined,
    acceptedAt: undefined,
    startedAt: undefined,
    activeBoundAt: undefined,
    legs: (source.legs ?? []).map((leg) => ({ ...leg, departureIso })),
  };
}

export function followingLeg(source: Mission, now = new Date()): Mission {
  const dep = new Date(source.departureIso);
  const arrival = Number.isFinite(dep.getTime())
    ? new Date(dep.getTime() + Math.max(0, source.eteMin) * 60_000)
    : now;
  const stamp = now.getTime().toString(36);
  return {
    ...copiedMission(source, now),
    id: `ns-leg-${source.id}-${stamp}`,
    departureIso: arrival.toISOString(),
    originIcao: source.destIcao,
    originName: source.destName,
    originCity: source.destCity,
    destIcao: source.originIcao,
    destName: source.originName,
    destCity: source.originCity,
    parentMissionId: source.parentMissionId || source.id,
    paxCount: 0,
    paxNames: [],
    flightType: "ferry",
    callsign: `${source.flightNumber || source.callsign || "LEG"}F`,
    flightNumber: `${source.flightNumber || source.callsign || "LEG"}F`,
    legs: [],
  };
}
